import { useState } from "react";
import { X, CheckCircle2, Loader2, MessageCircle, AlertTriangle } from "lucide-react";
import { submitFeedback, markFeedbackSubmitted } from "../lib/feedback";
import { formatApiError } from "../lib/authApi";

const EASE = [
  { value: "very_easy", label: "Very easy" },
  { value: "easy", label: "Easy" },
  { value: "confusing", label: "A bit confusing" },
  { value: "hard", label: "Hard" },
];

const ACCURACY = [
  { value: "spot_on", label: "Spot on" },
  { value: "mostly_right", label: "Mostly right" },
  { value: "some_errors", label: "Some errors" },
  { value: "way_off", label: "Way off" },
  { value: "didnt_check", label: "Didn't check" },
];

const WORKED = [
  { value: "upload_smooth", label: "Upload smooth" },
  { value: "report_clear", label: "Report clear" },
  { value: "red_flags_useful", label: "Red flags useful" },
  { value: "clarity_score_helpful", label: "Clarity score helpful" },
  { value: "ask_ai_good", label: "Ask AI good" },
  { value: "pdf_nice", label: "PDF nice" },
  { value: "looks_trustworthy", label: "Looks trustworthy" },
];

const DIDNT = [
  { value: "upload_failed", label: "Upload failed" },
  { value: "analysis_wrong", label: "Analysis wrong" },
  { value: "too_slow", label: "Too slow" },
  { value: "report_confusing", label: "Report confusing" },
  { value: "ask_ai_unhelpful", label: "Ask AI unhelpful" },
  { value: "login_issue", label: "Login issue" },
  { value: "mobile_issue", label: "Mobile issue" },
];

const RECOMMEND = [
  { value: "yes", label: "Yes" },
  { value: "maybe", label: "Maybe" },
  { value: "no", label: "No" },
];

function RadioGroup({ name, options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-group`}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onChange(o.value)}
            data-testid={`${testIdPrefix}-${o.value}`}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              active
                ? "bg-blue-600 border-blue-600 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50/40"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function CheckboxGrid({ name, options, values, onToggle, testIdPrefix }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`${testIdPrefix}-grid`}>
      {options.map((o) => {
        const active = values.includes(o.value);
        return (
          <button
            type="button"
            key={o.value}
            onClick={() => onToggle(o.value)}
            data-testid={`${testIdPrefix}-${o.value}`}
            className={`rounded-xl border px-3 py-2 text-left text-sm transition-colors ${
              active
                ? "border-blue-500 bg-blue-50 text-blue-900"
                : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded border mr-2 align-middle ${
                active ? "bg-blue-600 border-blue-600" : "border-slate-300"
              }`}
            />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function FeedbackModal({ open, onClose, uploadId }) {
  const [ease, setEase] = useState("");
  const [accuracy, setAccuracy] = useState("");
  const [worked, setWorked] = useState([]);
  const [didnt, setDidnt] = useState([]);
  const [wrongDetails, setWrongDetails] = useState("");
  const [recommend, setRecommend] = useState("");
  const [other, setOther] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ease || !accuracy || !recommend) {
      setError("Please answer the three required questions marked with *.");
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        upload_id: uploadId || null,
        ease,
        accuracy,
        recommend,
        what_worked: worked,
        what_didnt: didnt,
        wrong_details: wrongDetails.trim(),
        other: other.trim(),
        name: name.trim() || null,
      });
      if (uploadId) markFeedbackSubmitted(uploadId);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
      data-testid="feedback-modal-overlay"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="feedback-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2 px-5 py-4 border-b border-slate-100 sticky top-0 bg-white/95 backdrop-blur-sm z-10">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">
              How was this?
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              90 seconds. Helps us make it better.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="feedback-modal-close"
            className="p-2 rounded-lg text-slate-400 hover:text-slate-900 hover:bg-slate-100"
            aria-label="Close feedback"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="feedback-done">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h4 className="mt-4 font-display text-lg font-semibold text-slate-900">
              Thanks for the feedback!
            </h4>
            <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
              We read every response and use it to make KnowYourPolicy.ai better.
            </p>
            <button
              onClick={onClose}
              data-testid="feedback-done-close"
              className="mt-6 inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2 text-sm font-medium"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-5">
            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                How easy was it to use? <span className="text-red-500">*</span>
              </label>
              <RadioGroup
                name="ease"
                options={EASE}
                value={ease}
                onChange={setEase}
                testIdPrefix="fb-ease"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                Was the analysis accurate for your policy? <span className="text-red-500">*</span>
              </label>
              <RadioGroup
                name="accuracy"
                options={ACCURACY}
                value={accuracy}
                onChange={setAccuracy}
                testIdPrefix="fb-accuracy"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                What worked well?
              </label>
              <CheckboxGrid
                name="worked"
                options={WORKED}
                values={worked}
                onToggle={(v) => toggle(worked, setWorked, v)}
                testIdPrefix="fb-worked"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                What didn&apos;t work?
              </label>
              <CheckboxGrid
                name="didnt"
                options={DIDNT}
                values={didnt}
                onToggle={(v) => toggle(didnt, setDidnt, v)}
                testIdPrefix="fb-didnt"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                If the analysis got something wrong, what was it?
              </label>
              <textarea
                value={wrongDetails}
                onChange={(e) => setWrongDetails(e.target.value)}
                data-testid="fb-wrong-details"
                rows={3}
                placeholder="e.g. clarity score too low; missed an add-on…"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                maxLength={2000}
              />
            </div>

            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                Would you recommend it? <span className="text-red-500">*</span>
              </label>
              <RadioGroup
                name="recommend"
                options={RECOMMEND}
                value={recommend}
                onChange={setRecommend}
                testIdPrefix="fb-recommend"
              />
            </div>

            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                Anything else?
              </label>
              <textarea
                value={other}
                onChange={(e) => setOther(e.target.value)}
                data-testid="fb-other"
                rows={2}
                placeholder="Suggestions, missing features, kind words…"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                maxLength={2000}
              />
            </div>

            <div>
              <label className="block font-medium text-slate-900 text-sm mb-2">
                Your name <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="fb-name"
                placeholder="Optional"
                maxLength={80}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {error && (
              <div
                className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start gap-2"
                data-testid="feedback-error"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-full px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-50"
                data-testid="feedback-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="feedback-submit"
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-full px-5 py-2 text-sm font-semibold shadow-sm"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <MessageCircle className="h-3.5 w-3.5" />
                    Send feedback
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
